import Layout from "components/layout";
import Loader from "components/loader";
import Link from "next/link";
import { Fragment } from "react";

import Questionnaire from "components/pages/my_questionnaires/questionnaire";
import { questionnaires as questionnaireFixtures } from "components/pages/my_questionnaires/fixtures";
import InfiniteScrollable from "components/behaviors/infinite_scrollable";

import * as legacyQuestionnairePerformancesApi from "api/legacy_questionnaire_performances";

import { useUser } from "hooks/user";
import { useInfiniteQuery } from "react-query";
import { useMounted } from "hooks/react";

export default function MyQuestionnaires(): JSX.Element {
  const { user } = useUser();
  const mounted = useMounted();

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      "my_questionnaires",
      ({ pageParam = 1 }) =>
        legacyQuestionnairePerformancesApi.index(pageParam),
      {
        enabled: !!user,
        getNextPageParam: (lastPage, pages) => {
          return lastPage.body.questionnaires.length > 0
            ? pages.length + 1
            : undefined;
        }
      }
    );
  const pages = data?.pages || [
    { body: { questionnaires: questionnaireFixtures } }
  ];

  return (
    <Layout
      title="Meus simulados"
      description="Salvamos todos os simulados que você resolve para que possa revisá-los quando quiser!"
      canonical="/meus-simulados"
    >
      <main>
        <div className="container">
          {!user && (
            <p>
              Aqui você vai poder acessar e revisar todos os simulados
              realizados! Crie uma conta e comece agora. É de graça!
            </p>
          )}

          <InfiniteScrollable
            onPaginate={() => !!user && fetchNextPage()}
            loading={isFetchingNextPage}
          >
            <div className="flex flex-col md:flex-row md:flex-wrap">
              {pages.map((page, index) => (
                <Fragment key={index}>
                  {page.body.questionnaires.map((questionnaire) => {
                    return (
                      <Fragment key={questionnaire.id}>
                        <div
                          className="mb-5 w-full md:mb-0 md:p-2 md:w-1/2 lg:w-1/3"
                          data-disabled={!user}
                        >
                          <Questionnaire
                            {...questionnaire}
                            loading={!mounted || isLoading}
                          />
                        </div>
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </InfiniteScrollable>

          {isFetchingNextPage && (
            <div className="flex justify-center mt-6">
              <Loader />
            </div>
          )}

          <AllLoadedMessage
            pages={pages}
            hasNextPage={hasNextPage}
            user={user}
            isLoading={isLoading}
          />
        </div>
      </main>
    </Layout>
  );
}

function AllLoadedMessage({ isLoading, user, hasNextPage, pages }) {
  if (isLoading || !user || hasNextPage) return <></>;

  if (pages[0]?.body?.questionnaires?.length > 0) {
    return (
      <span className="block mt-5 w-full text-center">
        Todos os simulados foram carregados :)
      </span>
    );
  }

  return (
    <span className="flex flex-col items-center w-full text-center">
      Você ainda não completou nenhum simulado.
      <Link href="/questoes">
        <a className="btn btn-primary inline-block mt-4 w-full md:w-auto">
          Resolver simulado
        </a>
      </Link>
    </span>
  );
}
